import * as React from 'react';
import { Link as BaseLink } from 'react-router-dom';
import { ViewIntent } from '../common/props';
import * as Classes from '../common/classes';
import classNames from 'classnames';
import { Spinner } from '../spinner/Spinner';
type BaseLinkToPropsType = React.ComponentProps<typeof BaseLink>['to'];
type AsyncLinkProps = Promise<{ options: any; url: string }>;
type BaseLinkProps = React.ComponentProps<typeof BaseLink>;
export type LinkProps = Omit<BaseLinkProps, 'to'> & {
  /**
   * Typ informace.
   */
  intent?: ViewIntent;
  to: BaseLinkToPropsType | AsyncLinkProps;
};

export const Link = ({ intent, className, to, style, ...other }: LinkProps) => {
  const [asyncTo, setAsyncTo] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const computedClassName = classNames(className, Classes.LINK, {
    [Classes.TYPOGRAPHY_INHERIT]: !intent || intent === 'inherit',
    [Classes.TYPOGRAPHY_PRIMARY]: intent === 'primary',
    [Classes.TYPOGRAPHY_SECONDARY]: intent === 'secondary',
    [Classes.TYPOGRAPHY_DANGER]: intent === 'danger',
    [Classes.TYPOGRAPHY_SUCCESS]: intent === 'success',
    [Classes.TYPOGRAPHY_WARNING]: intent === 'warning',
    [Classes.TYPOGRAPHY_INACTIVE]: intent === 'inactive',
    [Classes.TYPOGRAPHY_DEFAULT]: intent === 'default',
    // [Classes.TYPOGRAPHY_INVERSE]: parentInverse,
  });
  React.useEffect(() => {
    let isCancelled = false;
    const asyncResolvePromise = async (promise: AsyncLinkProps) => {
      setIsLoading(true);
      try {
        const result = await promise;
        if (!isCancelled) {
          setAsyncTo(result.url);
          setIsLoading(false);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('Link: Failed to resolve async URL', error);
          setAsyncTo(null);
          setIsLoading(false);
        }
      }
    };
    if (to instanceof Promise) {
      asyncResolvePromise(to);
    } else {
      setAsyncTo(null);
      setIsLoading(false);
    }
    return () => {
      isCancelled = true;
    };
  }, [to]);
  const externalUrl = getExternalUrl(to);
  if (externalUrl) {
    return (
      <a
        style={style}
        {...other}
        className={computedClassName}
        href={externalUrl}
      />
    );
  }

  if (to instanceof Promise) {
    const { children, ...restOther } = other;
    if (isLoading || !asyncTo) {
      return (
        <span
          style={style}
          className={classNames(computedClassName, Classes.LINK_ASYNC_LOADING)}
          {...restOther}
        >
          {isLoading && <Spinner size={10} />}
          <span className={Classes.LINK_ASYNC_CHILDREN}>{children}</span>
        </span>
      );
    }
    return (
      <BaseLink
        style={style}
        to={asyncTo}
        className={computedClassName}
        {...other}
      />
    );
  }
  return (
    <BaseLink style={style} to={to} className={computedClassName} {...other} />
  );
};
const getExternalUrl = (
  to: BaseLinkToPropsType | AsyncLinkProps
): string | undefined => {
  if (to instanceof Promise) {
    //zbytečná podmínka, ale typescript si jinak stěžuje
    return undefined;
  }
  if (typeof to === 'string' && to.startsWith('http')) {
    return to;
  } else if (typeof to === 'object' && to.pathname?.startsWith('http')) {
    return to.pathname;
  }
  return undefined;
};
